import { useChatMessages } from '@ai-sdk-tools/store';
import { useChatStatus } from '@ai-sdk-tools/store';
import { useApolloClient } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import type { ChatOnFinishCallback } from 'ai';
import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useNovaChat } from '../hooks/use-nova-chat';
import { markChatStreamActive, normalizeChatUuid, updateChatsListAfterStream } from '../lib/notification-helpers';
import type { UIMessageWithMetadata } from '../lib/types';

interface ChatRootProps {
  children: ReactNode;
}

/**
 * Root component that sets up the chat context using @ai-sdk-tools/store.
 * This component initializes the chat hook and makes chat state available
 * to all child components via context.
 *
 * Reads chatUuid, initialMessages, and callbacks from context.
 * Syncs hasAnyMessages from AI SDK store to ChatOverlayContext so NovaProvider
 * can use it without needing access to the AI SDK Provider.
 *
 * @example
 * ```tsx
 * <ChatRoot>
 *   <ChatHeader>...</ChatHeader>
 *   <Messages />
 *   <ChatInput />
 * </ChatRoot>
 * ```
 */
export function ChatRoot({ children }: ChatRootProps) {
  const {
    chatUuid,
    initialMessages,
    callbacks,
    setHasAnyMessages,
    notificationController,
    isVisible,
    isStandaloneMode,
    setChatError,
    chatActiveStreamId,
    userUuid,
    effectiveAppType,
    isPublicMode,
    setRetryLastRequest,
  } = useChatOverlayContext();
  const apolloClient = useApolloClient();
  const messages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  const currentChatUuidRef = useRef<string | null>(null);
  const isChatVisibleRef = useRef<boolean | null>(null);
  const chatTitleRef = useRef<string>('New Chat');
  const previousStatusRef = useRef(status);

  useEffect(() => {
    currentChatUuidRef.current = chatUuid;
    isChatVisibleRef.current = isVisible;
  }, [chatUuid, isVisible]);

  // Extract chat title from messages for use in finish callback
  useEffect(() => {
    if (messages.length > 0) {
      const lastUserMessage = [...messages].reverse().find((msg) => msg.role === 'user');
      const title =
        lastUserMessage?.parts?.[0]?.type === 'text' && 'text' in lastUserMessage.parts[0]
          ? String(lastUserMessage.parts[0].text).slice(0, 50) || 'New Chat'
          : 'New Chat';
      chatTitleRef.current = title;
    }
  }, [messages]);

  // Wrap onFinish to integrate with notification controller
  const handleFinish: ChatOnFinishCallback<UIMessageWithMetadata> = useCallback(
    async (event) => {
      if (!event.isError) {
        setChatError(undefined);
        setRetryLastRequest(undefined);
      }

      await notificationController.onStreamFinish({
        chatUuid,
        isChatVisible: isChatVisibleRef.current ?? undefined,
        isStandaloneMode,
        currentChatUuid: currentChatUuidRef.current ?? undefined,
      });

      // Call original callback if provided
      callbacks?.onFinish?.();
    },
    [chatUuid, callbacks, notificationController, isStandaloneMode, setChatError, setRetryLastRequest],
  );

  // Handle stream errors by surfacing them to the UI via overlay context
  const handleError = useCallback(
    (error: Error) => {
      browserLogger.error('[ChatRoot] Stream error', {
        chatUuid,
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
      });

      const isConnectionError =
        error.message.includes('network') ||
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('connection') ||
        error.name === 'AbortError';

      setChatError({
        message: isConnectionError
          ? 'Unable to connect. Please refresh the page or check your network connection.'
          : 'Unable to complete your request. Please try again.',
        timestamp: new Date(),
        type: isConnectionError ? 'network' : 'server',
        canRetry: true,
      });

      callbacks?.onError?.(error);
    },
    [chatUuid, setChatError, callbacks],
  );

  const handleReconnectUnavailable = useCallback(
    (reconnectChatUuid: string) => {
      updateChatsListAfterStream({
        apolloClient,
        chatUuid: reconnectChatUuid,
        userUuid,
        appType: effectiveAppType,
      });
      notificationController.unregisterStream(reconnectChatUuid);
      if (chatUuid !== reconnectChatUuid && normalizeChatUuid(chatUuid) === reconnectChatUuid) {
        notificationController.unregisterStream(chatUuid);
      }
    },
    [apolloClient, chatUuid, effectiveAppType, notificationController, userUuid],
  );

  // Initialize chat hook - this makes chat state available via context
  useNovaChat({
    chatUuid,
    initialMessages,
    activeStreamId: chatActiveStreamId,
    onReconnectUnavailable: handleReconnectUnavailable,
    onFinish: handleFinish,
    onError: handleError,
  });

  useEffect(() => {
    setChatError(undefined);
    setRetryLastRequest(undefined);
  }, [chatUuid, setChatError, setRetryLastRequest]);

  // Sync hasAnyMessages from AI SDK store to ChatOverlayContext
  // This ensures NovaProvider always has the current message count
  useEffect(() => {
    setHasAnyMessages(messages.length > 0);
  }, [messages.length, setHasAnyMessages]);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    const wasActive = previousStatus === 'submitted' || previousStatus === 'streaming';
    const isActive = status === 'submitted' || status === 'streaming';

    if (!wasActive && isActive) {
      setChatError(undefined);
    }

    if (!isPublicMode && !wasActive && isActive) {
      notificationController.registerStream(chatUuid, isVisible, isStandaloneMode);
      markChatStreamActive({
        apolloClient,
        chatUuid,
        userUuid,
        appType: effectiveAppType,
      });
    }

    previousStatusRef.current = status;
  }, [
    apolloClient,
    chatUuid,
    effectiveAppType,
    isPublicMode,
    isStandaloneMode,
    isVisible,
    notificationController,
    setChatError,
    status,
    userUuid,
  ]);

  return children;
}
